import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import FormLabel from "@mui/material/FormLabel";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import { isArray } from "lodash-es";
import React from "react";
import { Accept } from "react-dropzone";
import { useFormContext, Controller } from "react-hook-form";

import { colors } from "theme";

import { Dropzone } from "./components/Dropzone";
import { FileItem } from "./components/FileItem";

interface FileFormFieldProps {
  name: string;
  label?: string;
  tooltip?: React.ReactNode;
  accept: Accept;
  multiple?: boolean;
  typeLabel?: string;
  hideItems?: boolean;
  hideWeight?: boolean;
  isItemLoading?: boolean;
}

export const FileFormField = ({
  name,
  label,
  multiple,
  tooltip,
  hideItems,
  isItemLoading,
  ...rest
}: FileFormFieldProps) => {
  const { control, getValues, setValue, trigger } = useFormContext();

  const filterItemFromFieldValues = async (index: number) => {
    const files = getValues(name) as File[];

    if (isArray(files)) {
      const newFiles = files.filter((_, i) => i !== index);
      setValue(name, newFiles);
    } else {
      setValue(name, null);
    }

    await trigger(name);
  };

  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange }, fieldState: { error } }) => {
        const files = value as File | File[] | undefined;
        const items = Array.isArray(files) ? files : files ? [files] : [];

        return (
          <>
            {(label || tooltip) && (
              <Stack alignItems="flex-start" direction="row" gap={1} mb={2}>
                {label && (
                  <FormLabel
                    htmlFor={name}
                    sx={{
                      color: "text.primary"
                    }}
                  >
                    {label}
                  </FormLabel>
                )}

                {tooltip}
              </Stack>
            )}

            {error && (
              <Stack
                alignItems="flex-start"
                direction="row"
                gap={1}
                py={1.5}
                px={2}
                mb={2}
                borderRadius={1}
                width={"100%"}
                bgcolor={colors.redBackground}
              >
                <ErrorOutlineIcon color="error" />

                <Typography
                  sx={{
                    color: colors.redBackgroundText
                  }}
                >
                  {error.message}
                </Typography>
              </Stack>
            )}

            {items.length === 0 && (
              <Dropzone
                {...rest}
                id={name}
                onDrop={(acceptedFiles) => {
                  onChange(multiple ? acceptedFiles : acceptedFiles[0] ?? null);

                  trigger(name);
                }}
              />
            )}

            {isItemLoading && (
              <Skeleton
                variant="rectangular"
                width={690}
                height={78}
                sx={{
                  my: 3,
                  borderRadius: 1
                }}
              />
            )}

            {hideItems
              ? null
              : items.map((file, index) => (
                  <FileItem
                    key={file.name}
                    index={index}
                    file={file}
                    onRemove={filterItemFromFieldValues}
                    showSize={!rest.hideWeight}
                  />
                ))}
          </>
        );
      }}
    />
  );
};
